import React, { createContext, ReactNode, useContext, useReducer } from 'react'
import { getStoredPostcode } from '../../utils/search/local-search.util'
import { FacetName } from '../../__generated__/fed-graphql-types'
import { ExpandableGroupName } from '../../components/expandable-filter/types'
import { isEqual } from 'lodash-es'
import { useLocation } from 'react-router-dom'

type FacetStateKeyType = FacetName | undefined
type FacetStateValueType = {
    open?: boolean
    close?: boolean
}

type FormState = {
    facetState: Map<FacetStateKeyType, FacetStateValueType>
    currentlyExpandedByGroups: ExpandableGroupName[]
}

type FormStateAction =
    | {
          type: 'expand_filter'
          expandableGroupName: ExpandableGroupName
      }
    | {
          type: 'toggle_filter'
          expandableGroupName: ExpandableGroupName
      }
    | {
          type: 'expand_filters'
          expandableGroupNames: ExpandableGroupName[]
      }
    | {
          type: 'collapse_filters'
      }

type FormStateContext = {
    state: FormState
    expandFilterGroup: (expandableGroupName: ExpandableGroupName) => void
    toggleFilterGroup: (expandableGroupName: ExpandableGroupName) => void
    expandAllFilterGroups: (expandableGroupNames: ExpandableGroupName[]) => void
    collapseFilterGroups: () => void
    isFilterGroupOpen: (expandableGroupName: ExpandableGroupName) => boolean
}

const defaultState = {
    facetState: new Map(),
    postcode: getStoredPostcode(),
    location: false,
    currentlyExpandedByGroups: [],
}

const defaultValue: FormStateContext = {
    state: defaultState,
    expandFilterGroup: () => {},
    toggleFilterGroup: () => {},
    expandAllFilterGroups: () => {},
    collapseFilterGroups: () => {},
    isFilterGroupOpen: () => false,
}

const FormStateContext = createContext<FormStateContext>(defaultValue)

const formStateReducer = (state: FormState, action: FormStateAction): FormState => {
    switch (action.type) {
        case 'expand_filter':
            if (state.currentlyExpandedByGroups.includes(action.expandableGroupName)) {
                return state
            }

            return {
                ...state,
                currentlyExpandedByGroups: [...state.currentlyExpandedByGroups, action.expandableGroupName],
            }
        case 'toggle_filter':
            const newCurrentlyExpandedByGroup = state.currentlyExpandedByGroups.includes(action.expandableGroupName)
                ? state.currentlyExpandedByGroups.filter((item: string) => item !== action.expandableGroupName)
                : [...state.currentlyExpandedByGroups, action.expandableGroupName]
            return {
                ...state,
                currentlyExpandedByGroups: newCurrentlyExpandedByGroup,
            }
        case 'expand_filters':
            if (isEqual(state.currentlyExpandedByGroups, action.expandableGroupNames)) {
                return state
            }
            return {
                ...state,
                currentlyExpandedByGroups: action.expandableGroupNames,
            }
        case 'collapse_filters':
            return {
                ...state,
                currentlyExpandedByGroups: [],
            }
    }
}

const FormState = (props: { children: ReactNode }) => {
    const [state, dispatch] = useReducer(formStateReducer, defaultState)
    const { search } = useLocation()
    const urlParams = new URLSearchParams(search)
    const allOpen = urlParams.get('overrideExpandAllFilters') === 'true'
    const { currentlyExpandedByGroups } = state

    const value: FormStateContext = {
        state,
        expandFilterGroup: (expandableGroupName: ExpandableGroupName) => {
            dispatch({ type: 'expand_filter', expandableGroupName })
        },
        toggleFilterGroup: (expandableGroupName: ExpandableGroupName) => {
            dispatch({ type: 'toggle_filter', expandableGroupName })
        },
        expandAllFilterGroups: (expandableGroupNames: ExpandableGroupName[]) => {
            dispatch({ type: 'expand_filters', expandableGroupNames })
        },
        collapseFilterGroups: () => {
            dispatch({ type: 'collapse_filters' })
        },
        isFilterGroupOpen: (expandableGroupName: ExpandableGroupName) => {
            return allOpen || currentlyExpandedByGroups.includes(expandableGroupName)
        },
    }
    return <FormStateContext.Provider value={value}>{props.children}</FormStateContext.Provider>
}

const useFormState = () => {
    return useContext(FormStateContext)
}

export { FormState, useFormState }
